import type { WebRuntimeConfig } from '@bnb/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { createContext, use, useSyncExternalStore } from 'react';
import type { AuthClient } from './auth.ts';
import type { LiveConnection, LiveState } from './live.ts';
import type { Outbox, OutboxSnapshot } from './outbox.ts';
import type { ApiClient } from './trpc.ts';

/**
 * Everything the app wires up once at start-up (src/main.tsx) and shares with screens.
 * Also passed to the router as context, so route guards can check auth.
 */
export interface AppServices {
  config: WebRuntimeConfig;
  auth: AuthClient;
  queryClient: QueryClient;
  trpcClient: ApiClient;
  outbox: Outbox;
  live: LiveConnection;
}

export const ServicesContext = createContext<AppServices | null>(null);

export function useServices(): AppServices {
  const services = use(ServicesContext);
  if (!services) throw new Error('useServices must be used inside <ServicesContext>');
  return services;
}

/** Live view of the offline outbox (pending count, rejected entries, flushing). */
export function useOutboxSnapshot(): OutboxSnapshot {
  const { outbox } = useServices();
  return useSyncExternalStore(outbox.subscribe, outbox.getSnapshot, outbox.getSnapshot);
}

export function useLiveState(): LiveState {
  const { live } = useServices();
  return useSyncExternalStore(live.subscribe, live.getState, live.getState);
}

function subscribeOnline(listener: () => void) {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );
}
